import { useState, useEffect } from "react";
import getGraphOverviewData from "../apis/contentApis/getGraphOverviewData";
import { openNotification } from "../utils/UI/openNotification";
// Define interfaces for our data structures
interface DataItem {
  pubDate: string;
  print?: any;
  digital?: any;
  tv?: any;
  radio?: any;
  [key: string]: any;
}

interface SentimentCounts {
  positive: number;
  neutral: number;
  negative: number;
}

interface SentimentDataItem {
  pubDate: string;
  total_sentiment_score: number;
  sentiment_counts: SentimentCounts;
}

interface AVEDataItem {
  pubDate: string;
  ave: number;
}

interface ColoredTilesData {
  print: AVEDataItem[];
  digital: AVEDataItem[];
  radio: AVEDataItem[];
  tv: AVEDataItem[];
}

interface APIResponse {
  status: boolean;
  message: string;
  data: {
    print: DataItem[];
    digital: DataItem[];
    radio: DataItem[];
    tv: DataItem[];
  };
  tableData: DataItem[];
  NewsMediaPublicationShare: number[];
  languageStats: any[];
  linechart: DataItem[];
  digitalData1: any[];
  balckcart: any[];
  SentimentData: SentimentDataItem[];
}

const useGraphHook = (digitalFilter: any) => {
  // Define state with explicit types
  const [graphdata, setGraphdata] = useState<DataItem[]>([]);
  const [graphdata1, setGraphdata1] = useState<DataItem[]>([]);
  const [NewsMediaPublicationShares, setNewsMediaPublicationShares] = useState<
    number[]
  >([]);
  const [digitalPublicationsdata, setDigitalPublicationsdata] = useState<
    DataItem[]
  >([]);
  const [language, setLanguage] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cartdata1, setCartdata1] = useState<any[]>([]);
  const [linechart, setLinechart] = useState<DataItem[]>([]);
  const [blackcart, setBlackcart] = useState<any[]>([]);
  const [sentimentData, setSentimentData] = useState<SentimentDataItem[]>([]);
  const [coloredTilesChart, setColoredTilesChart] = useState<ColoredTilesData>({
    print: [],
    digital: [],
    radio: [],
    tv: []
  });

  // Helper function to sort data by date
  const sortByDate = <T extends { pubDate: string }>(data: T[]): T[] => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.pubDate);
      const dateB = new Date(b.pubDate);
      return dateA.getTime() - dateB.getTime();
    });
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = (await getGraphOverviewData(
        digitalFilter
      )) as APIResponse;

      if (!response?.status) throw response;

      // Sort the data arrays by date before setting state
      const sortedLineChart = sortByDate(response.linechart || []);
      const sortedSentimentData = sortByDate(response.SentimentData || []);
      const sortedTableData = sortByDate(response.tableData || []);
      const sortedGraphData = response.data?.digital
        ? sortByDate(response.data.digital)
        : [];

      // Process colored tiles data
      const coloredTilesData: ColoredTilesData = {
        print: [],
        digital: [],
        radio: [],
        tv: []
      };
      
      // Process print data
      if (response.data.print && response.data.print.length > 0) {
        coloredTilesData.print = response.data.print.map(item => ({
          pubDate: item.pubDate,
          ave: item.totalAve
        }));
      }
      
      // Process digital data
      if (response.data.digital && response.data.digital.length > 0) {
        coloredTilesData.digital = response.data.digital.map(item => ({
          pubDate: item.pubDate,
          ave: item.totalAve
        }));
      }
      
      // Process radio data
      if (response.data.radio && response.data.radio.length > 0) {
        coloredTilesData.radio = response.data.radio.map(item => ({
          pubDate: item.pubDate,
          ave: item.totalAve
        }));
      }
      
      // Process tv data
      if (response.data.tv && response.data.tv.length > 0) {
        coloredTilesData.tv = response.data.tv.map(item => ({
          pubDate: item.pubDate,
          ave: item.totalAve
        }));
      }

      // Set all the state values with proper typing
      setNewsMediaPublicationShares(response.NewsMediaPublicationShare || []);
      setDigitalPublicationsdata(sortedTableData);
      setLanguage(response.languageStats || []);
      setGraphdata(sortedGraphData);
      setGraphdata1(sortedGraphData);
      setCartdata1(response.digitalData1 || []);
      setLinechart(sortedLineChart);
      setBlackcart(response.balckcart || []);
      setSentimentData(sortedSentimentData);
      setColoredTilesChart(coloredTilesData);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      // openNotification("error", {
      //   message: err?.message || "Failed to fetch articles!",
      // });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!digitalFilter) return;
    fetchArticles();
  }, [digitalFilter]);

  return {
    graphdata,
    graphdata1,
    blackcart,
    linechart,
    cartdata1,
    language,
    NewsMediaPublicationShares,
    digitalPublicationsdata,
    loading,
    sentimentData,
    coloredTilesChart,
  };
};

export default useGraphHook;
